/**
 * Comprehensive Attendance Report
 * ---------------------------------
 * - Custom date-range, per-employee or entity-wide
 * - Shows check-in, check-out, work hours, distance, late / early minutes,
 *   half-day & approval status.
 * - Employees can view ONLY their own; Admin/HR/HOD see scoped sub-tree.
 * - Exports to CSV.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Loader2, Download, FileText, Calendar, Clock, AlertTriangle, Users } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';
import { fullName } from '../lib/fullName';

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStartStr = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const fmtTime = (t) => (t ? t.slice(0, 5) : '—');

export function ComprehensiveAttendanceReport({ entityId, currentUser }) {
  const role = currentUser?.role || 'employee';
  const isPriv = ['super_admin', 'admin', 'hr', 'principal', 'hod', 'branch_admin'].includes(role);

  const [startDate, setStartDate] = useState(monthStartStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  // Load employees list (privileged users only) for the picker
  useEffect(() => {
    if (!isPriv) return;
    const load = async () => {
      try {
        const url = new URL(`${API_URL}/api/employees`);
        if (entityId) url.searchParams.set('entity_id', entityId);
        const res = await fetch(url, { headers: headers(), credentials: 'include' });
        const data = await safeJson(res);
        if (Array.isArray(data)) {
          setEmployees(data);
          const depts = Array.from(new Set(data.map(e => e.department).filter(Boolean))).sort();
          setDepartments(depts);
        }
      } catch (err) {
        // ignore — picker is optional UX
        void err;
      }
    };
    load();
  }, [entityId, isPriv]);

  const runReport = useCallback(async () => {
    if (!startDate || !endDate) {
      toast.error('Please pick both Start Date and End Date');
      return;
    }
    if (startDate > endDate) {
      toast.error('Start date must be on or before End date');
      return;
    }
    setLoading(true);
    try {
      const url = new URL(`${API_URL}/api/attendance/comprehensive-report`);
      url.searchParams.set('start_date', startDate);
      url.searchParams.set('end_date', endDate);
      if (isPriv && employeeFilter && employeeFilter !== 'all') url.searchParams.set('employee_id', employeeFilter);
      if (isPriv && departmentFilter && departmentFilter !== 'all') url.searchParams.set('department', departmentFilter);
      if (isPriv && entityId) url.searchParams.set('entity_id', entityId);

      const res = await fetch(url, { headers: headers(), credentials: 'include' });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch report');
      setReport(data);
      if (!data.records || data.records.length === 0) {
        toast.info('No attendance records found for the selected filters.');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, employeeFilter, departmentFilter, entityId, isPriv]);

  // Auto-run on mount with default range for fast first paint
  useEffect(() => {
    runReport();
  }, [runReport]);

  const exportCsv = () => {
    if (!report || !report.records || report.records.length === 0) {
      toast.error('Nothing to export — run a report first.');
      return;
    }
    const cols = [
      'Date', 'Employee ID', 'Employee Name', 'Department', 'Check In', 'Check Out',
      'Work Hours', 'Status', 'Late (min)', 'Early In (min)', 'Early Out (min)', 'Late Out (min)',
      'Half Day', 'Distance (m)', 'Inside Geofence', 'Face Verified', 'Remarks'
    ];
    const lines = [cols.join(',')];
    for (const r of report.records) {
      const row = [
        r.date,
        r.employee_id,
        `"${(r.employee_name || '').replace(/"/g, '""')}"`,
        `"${(r.department || '').replace(/"/g, '""')}"`,
        fmtTime(r.check_in),
        fmtTime(r.check_out),
        r.work_hours || 0,
        r.status || '',
        r.minutes_late || 0,
        r.minutes_early_in || 0,
        r.minutes_early_out || 0,
        r.minutes_late_out || 0,
        r.half_day || '',
        r.distance_from_campus ?? '',
        r.inside_geofence ? 'Yes' : 'No',
        r.face_verified ? 'Yes' : 'No',
        `"${(r.remarks || '').replace(/"/g, '""')}"`,
      ];
      lines.push(row.join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `attendance_report_${startDate}_to_${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const s = report?.summary || {};

  return (
    <div className="space-y-4" data-testid="comprehensive-attendance-report">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#DC2626]" />
            Comprehensive Attendance Report
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ca-start" className="text-xs">Start Date</Label>
              <Input
                id="ca-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="ca-start-date"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ca-end" className="text-xs">End Date</Label>
              <Input
                id="ca-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="ca-end-date"
              />
            </div>

            {isPriv && (
              <div className="space-y-1">
                <Label className="text-xs">Employee</Label>
                <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                  <SelectTrigger data-testid="ca-employee-filter"><SelectValue placeholder="All employees" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="all">All employees</SelectItem>
                    {employees.map(e => (
                      <SelectItem key={e.employee_id} value={e.employee_id}>
                        {fullName(e)} ({e.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {isPriv && departments.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Department</Label>
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger data-testid="ca-department-filter"><SelectValue placeholder="All departments" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="all">All departments</SelectItem>
                    {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs invisible">.</Label>
              <div className="flex gap-2">
                <Button
                  onClick={runReport}
                  disabled={loading}
                  className="bg-[#DC2626] hover:bg-[#B91C1C] text-white flex-1"
                  data-testid="ca-run-report-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate'}
                </Button>
                <Button
                  variant="outline"
                  onClick={exportCsv}
                  disabled={!report || (report.records || []).length === 0}
                  data-testid="ca-export-csv-btn"
                  title="Export to CSV"
                >
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Summary */}
          {report && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="ca-summary-cards">
              <SummaryTile label="Records" value={s.total_records || 0} icon={<Users className="w-3 h-3" />} />
              <SummaryTile label="Present" value={s.present || 0} color="green" />
              <SummaryTile label="Absent" value={s.absent || 0} color="red" />
              <SummaryTile label="On Leave" value={s.on_leave || 0} color="amber" />
              <SummaryTile label="Late marks" value={s.late || 0} color="orange" />
              <SummaryTile label="Total Late (min)" value={s.total_late_minutes || 0} color="red" />
              <SummaryTile label="Half-days" value={s.half_day || 0} color="orange" />
              <SummaryTile label="Early-out (min)" value={s.total_early_out_minutes || 0} color="amber" />
              <SummaryTile label="Work Hours" value={`${s.total_work_hours || 0}h`} color="blue" />
              <SummaryTile label="Avg Distance (m)" value={s.avg_distance_from_campus_m || 0} color="blue" />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Records</span>
            <span className="text-xs font-normal text-slate-500">
              {report ? `${report.records?.length || 0} rows · ${startDate} → ${endDate}` : 'Generate a report to view records'}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-8 text-slate-500 text-sm" data-testid="ca-loading">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading report…
            </div>
          ) : !report || (report.records || []).length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm" data-testid="ca-empty">
              No attendance records found. Try widening the date range.
            </div>
          ) : (
            <Table className="min-w-[900px]" data-testid="ca-records-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  {isPriv && <TableHead>Employee</TableHead>}
                  <TableHead>Check In</TableHead>
                  <TableHead>Check Out</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Distance</TableHead>
                  <TableHead>Late/Early</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.records.map(r => (
                  <TableRow key={r.attendance_id} data-testid={`ca-row-${r.attendance_id}`}>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{r.date}</TableCell>
                    {isPriv && (
                      <TableCell className="whitespace-nowrap">
                        <div className="text-sm font-medium">{r.employee_name}</div>
                        <div className="text-xs text-slate-500">{r.department || '—'}</div>
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-xs">{fmtTime(r.check_in)}</TableCell>
                    <TableCell className="font-mono text-xs">{fmtTime(r.check_out)}</TableCell>
                    <TableCell className="text-xs">{(r.work_hours || 0).toFixed(2)}h</TableCell>
                    <TableCell className="text-xs">
                      {r.distance_from_campus != null ? `${Math.round(r.distance_from_campus)}m` : '—'}
                      {r.inside_geofence === false && (
                        <Badge variant="outline" className="ml-1 text-[10px] border-red-300 text-red-700">Outside</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {r.minutes_late > 0 && (
                          <Badge className="bg-red-100 text-red-700 text-[10px]" data-testid="ca-late-badge">
                            <Clock className="w-2.5 h-2.5 mr-0.5" /> {r.minutes_late}m late
                          </Badge>
                        )}
                        {r.minutes_early_in > 0 && (
                          <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                            +{r.minutes_early_in}m early
                          </Badge>
                        )}
                        {r.minutes_early_out > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                            -{r.minutes_early_out}m early out
                          </Badge>
                        )}
                        {r.minutes_late_out > 0 && (
                          <Badge className="bg-orange-100 text-orange-700 text-[10px]">
                            +{r.minutes_late_out}m late out
                          </Badge>
                        )}
                        {(!r.minutes_late && !r.minutes_early_in && !r.minutes_early_out && !r.minutes_late_out) && (
                          <span className="text-[10px] text-slate-400">on time</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            r.status === 'present' ? 'border-green-300 text-green-700' :
                            r.status === 'absent' ? 'border-red-300 text-red-700' :
                            r.status === 'on_leave' ? 'border-amber-300 text-amber-700' :
                            'border-slate-300 text-slate-700'
                          }`}
                        >
                          {r.status || '—'}
                        </Badge>
                        {r.half_day && (
                          <Badge className="bg-orange-100 text-orange-700 text-[10px]">
                            {r.half_day === 'morning_half' ? 'AM half' : 'PM half'}
                          </Badge>
                        )}
                        {r.requires_approval && (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                            <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Pending
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryTile({ label, value, color = 'slate', icon }) {
  const colorMap = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <div className={`p-2 rounded-lg border ${colorMap[color] || colorMap.slate}`}>
      <div className="text-[10px] uppercase tracking-wide font-medium flex items-center gap-1">{icon}{label}</div>
      <div className="text-lg font-bold leading-tight">{value}</div>
    </div>
  );
}

export default ComprehensiveAttendanceReport;
